/**
 * Блок «Визуалы МП» во вкладке «Производство»: SKU-запуски визуалов артикула (воронки серий: состояние,
 * превью первого слайда, переход на страницу запуска, где слайды утверждают), прежние слайды карточки
 * (у товаров, чьи визуалы сделаны старым запуском; к ним же относится сцена набора) и черновики карточек по
 * площадкам и кабинетам с причинами блокировки. «Сделать визуалы» открывает те же шаги, что мастер «Создать
 * пряники» (для одного артикула). Публикации на площадки здесь нет: черновики остаются в «Черновиках ПИМ».
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, ExternalLink, ImageOff, Images, Loader2 } from 'lucide-react'
import { Badge } from '../../../components/ui/Badge'
import { Button } from '../../../components/ui/Button'
import { Lightbox } from '../../../components/ui/Lightbox'
import { AuthorizedMediaImage } from '../../../shared/media/authorizedMedia'
import { useAuthStore } from '../../../stores/authStore'
import { gingerbreadCatalogAPI } from '../api'
import { useSkuRuns } from '../visuals-cards/hooks'
import VisualsCardsDialog from '../visuals-cards/VisualsCardsDialog'
import { SkuRunsTable } from '../visuals-cards/VisualsStep'
import {
  blockerHint,
  draftStatusView,
  marketplaceLabel,
  MARKETPLACE_HREF,
  slideLabel,
  VISUALS_STATUS_LABEL,
  VISUALS_STATUS_VARIANT,
  type GingerbreadVisuals,
  type VisualsDraft,
} from '../visuals'
import { KitSceneBlock } from './KitSceneBlock'

/** Пока прежние визуалы создаются, статус перечитывается с этим интервалом. */
const POLL_MS = 5000

const sectionTitle = 'mb-2 text-2xs font-semibold uppercase tracking-wider text-gray-500 dark:text-zinc-400'

function DraftBlockers({ draft }: { draft: VisualsDraft }) {
  if (draft.blockers.length === 0) return <span className="text-xs text-gray-400 dark:text-zinc-500">—</span>
  return (
    <ul className="space-y-1.5">
      {draft.blockers.map((blocker, index) => {
        const hint = blockerHint(blocker.code)
        return (
          <li key={`${blocker.code}-${index}`} className="text-xs text-gray-700 dark:text-zinc-300">
            <span className="flex items-start gap-1.5">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-yellow-600 dark:text-yellow-400" />
              <span>
                {blocker.message || blocker.code}
                {blocker.code && blocker.message && (
                  <> <span className="font-mono text-2xs text-gray-400 dark:text-zinc-500">({blocker.code})</span></>
                )}
              </span>
            </span>
            {hint && <span className="mt-0.5 block pl-5 text-gray-500 dark:text-zinc-400">Что сделать: {hint}</span>}
          </li>
        )
      })}
    </ul>
  )
}

export function MarketplaceVisualsSection({ article, kit = false }: {
  article: string
  /** Набор (PP1): показывается блок ИИ-сцены «Набор на торте». */
  kit?: boolean
}) {
  const permissions = useAuthStore((state) => state.user?.permissions || [])
  const canRead = permissions.includes('Gingerbread:READ')
  // Сцена — платная операция ИИ: запуск только с правом APPROVE, как у остальных платных шагов генератора пряников.
  const canApprove = permissions.includes('Gingerbread:APPROVE')
  // Визуалы пряников — SKU-воронки универсального движка: читает ContentFunnel:READ, запускает ContentFunnel:UPDATE.
  const canReadRuns = permissions.includes('ContentFunnel:READ')
  const canRunFunnel = permissions.includes('ContentFunnel:UPDATE')

  const [data, setData] = useState<GingerbreadVisuals | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [lightbox, setLightbox] = useState<number | null>(null)
  const articles = useMemo(() => [article], [article])
  const runs = useSkuRuns(articles, canRead && canReadRuns)

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true)
      try {
        setData(await gingerbreadCatalogAPI.getVisuals(article))
        setError(null)
      } catch (cause: any) {
        if (!silent) setError(cause?.response?.data?.error?.message || cause?.message || 'Не удалось загрузить визуалы')
      } finally {
        if (!silent) setLoading(false)
      }
    },
    [article],
  )

  useEffect(() => {
    if (!canRead) return
    setData(null)
    void load()
  }, [canRead, load])

  const rendering = data?.visualsStatus === 'rendering'
  useEffect(() => {
    if (!rendering) return
    const timer = window.setInterval(() => void load(true), POLL_MS)
    return () => window.clearInterval(timer)
  }, [rendering, load])

  if (!canRead) return null

  const slides = data?.slides ?? []
  const status = data?.visualsStatus ?? 'none'

  const closeDialog = () => {
    setDialogOpen(false)
    // Запуски и черновики могли измениться, пока окно было открыто.
    void runs.refresh()
    void load(true)
  }

  return (
    <section className="md:col-span-2" aria-label="Визуалы МП" data-testid="marketplace-visuals-section">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h4 className="flex items-center gap-2 text-2xs font-semibold uppercase tracking-wider text-gray-500 dark:text-zinc-400">
          Визуалы МП
          {data && status !== 'none' && <Badge variant={VISUALS_STATUS_VARIANT[status]}>{VISUALS_STATUS_LABEL[status]}</Badge>}
        </h4>
        {canRunFunnel && (
          <Button size="sm" variant="primary" icon={<Images className="h-3.5 w-3.5" />} onClick={() => setDialogOpen(true)}>
            Сделать визуалы
          </Button>
        )}
      </div>

      {canReadRuns && (
        <div className="mb-4" data-testid="visuals-runs">
          <h5 className={sectionTitle}>Запуски визуалов</h5>
          {runs.error ? (
            <div role="alert" className="flex flex-wrap items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
              <span>{runs.error}</span>
              <Button size="sm" variant="secondary" onClick={() => void runs.refresh()}>Повторить</Button>
            </div>
          ) : runs.loading && runs.runs.length === 0 ? (
            <div className="flex items-center justify-center py-4 text-gray-400">
              <Loader2 className="h-5 w-5 animate-spin" aria-label="Загрузка запусков визуалов" />
            </div>
          ) : (
            <>
              <SkuRunsTable runs={runs.runs} />
              {runs.runs.length > 0 && (
                <p className="mt-2 text-xs text-gray-500 dark:text-zinc-400">
                  Утвердите визуалы на странице запуска — после публикации можно создавать карточки.
                </p>
              )}
            </>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" aria-label="Загрузка визуалов" />
        </div>
      ) : error ? (
        <div role="alert" className="flex flex-wrap items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
          <span>{error}</span>
          <Button size="sm" variant="secondary" onClick={() => void load()}>Повторить</Button>
        </div>
      ) : (
        <div className="space-y-4">
          {rendering && (
            <p className="flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-400">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Слайды создаются, страница обновится сама.
            </p>
          )}
          {status === 'failed' && (
            <p role="alert" className="text-xs text-red-600 dark:text-red-400">
              Не удалось создать слайды. Запустите создание ещё раз.
            </p>
          )}

          {slides.length > 0 && (
            <ul className="grid grid-cols-3 gap-2 sm:grid-cols-4" aria-label="Слайды карточки">
              {slides.map((slide, position) => (
                <li key={`${slide.index}-${slide.url}`}>
                  <button
                    type="button"
                    onClick={() => setLightbox(position)}
                    aria-label={`Открыть: ${slideLabel(slide, position + 1)}`}
                    className="group block w-full text-left focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <span className="flex aspect-[3/4] items-center justify-center overflow-hidden rounded-md border border-gray-200 bg-gray-50 dark:border-zinc-800 dark:bg-zinc-800">
                      <AuthorizedMediaImage
                        src={slide.url}
                        alt={slideLabel(slide, position + 1)}
                        className="h-full w-full object-cover"
                        errorFallback={<ImageOff aria-label="Слайд недоступен" className="h-8 w-8 text-gray-300 dark:text-zinc-600" />}
                      />
                    </span>
                    <span className="mt-1 block truncate text-2xs text-gray-500 dark:text-zinc-400">
                      {slideLabel(slide, position + 1)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {kit && (
            <KitSceneBlock article={article} canRun={canApprove} onSceneReady={() => void load(true)} />
          )}

          <div>
            <h5 className={sectionTitle}>Черновики карточек</h5>
            {(data?.drafts.length ?? 0) > 0 ? (
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-zinc-800">
                <table className="w-full text-left text-sm" aria-label="Черновики карточек по площадкам">
                  <thead className="bg-gray-50 text-xs text-gray-500 dark:bg-zinc-900 dark:text-zinc-400">
                    <tr>
                      <th scope="col" className="px-3 py-2 font-medium">Площадка</th>
                      <th scope="col" className="px-3 py-2 font-medium">Кабинет</th>
                      <th scope="col" className="px-3 py-2 font-medium">Статус</th>
                      <th scope="col" className="px-3 py-2 font-medium">Причины блокировки</th>
                      <th scope="col" className="px-3 py-2 font-medium"><span className="sr-only">Действие</span></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                    {data!.drafts.map((draft, index) => {
                      const view = draftStatusView(draft.status)
                      return (
                        <tr key={`${draft.marketplaceRaw}-${draft.sellerId ?? ''}-${index}`} className="align-top">
                          <td className="whitespace-nowrap px-3 py-2 text-gray-900 dark:text-zinc-100">{marketplaceLabel(draft)}</td>
                          <td className="px-3 py-2 text-gray-700 dark:text-zinc-300">{draft.sellerName || draft.sellerId || '—'}</td>
                          <td className="px-3 py-2"><Badge variant={view.variant}>{view.label}</Badge></td>
                          <td className="px-3 py-2"><DraftBlockers draft={draft} /></td>
                          <td className="whitespace-nowrap px-3 py-2 text-right">
                            {draft.marketplace && (
                              <Link
                                to={MARKETPLACE_HREF[draft.marketplace]}
                                title="Откроется каталог площадки: вкладка «Черновики PIM»"
                                className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline dark:text-indigo-400"
                              >
                                <ExternalLink className="h-3.5 w-3.5" />
                                Открыть в «Черновиках ПИМ»
                              </Link>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-xs text-gray-500 dark:text-zinc-400">Черновиков карточек пока нет.</p>
            )}
          </div>
        </div>
      )}

      <Lightbox
        isOpen={lightbox != null}
        items={slides.map((slide, position) => ({ src: slide.url, label: slideLabel(slide, position + 1) }))}
        index={lightbox ?? 0}
        onIndexChange={setLightbox}
        onClose={() => setLightbox(null)}
      />
      <VisualsCardsDialog isOpen={dialogOpen} articles={articles} onClose={closeDialog} />
    </section>
  )
}
